import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { createSelector } from "./selector";
import { State, Selector } from "./types";

const initialState: State = {
  calendarsHolidays: [],
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
};

function handleRequest(state: State): State {
  return { ...state, request: setRequestStateToPending() };
}

function handleSuccess(
  state: State,
  { calendarsHolidays }: Boundaries["retrieveCalendarsHolidays"]["success"],
): State {
  return { ...state, calendarsHolidays, request: setRequestStateToSuccess() };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleRemoveCalendarHolidaysRequest(
  state: State,
  calendarHolidays: Boundaries["removeCalendarHolidays"]["request"],
): State {
  return {
    ...state,
    calendarsHolidays: state.calendarsHolidays.filter(
      ({ uuid }) => uuid !== calendarHolidays.uuid,
    ),
  };
}

function handleRemoveCalendarHolidaysSuccess(
  state: State,
  calendarHolidays: Boundaries["removeCalendarHolidays"]["success"],
): State {
  return {
    ...state,
    calendarsHolidays: state.calendarsHolidays.filter(
      ({ uuid }) => uuid !== calendarHolidays.uuid,
    ),
  };
}

function handleRemoveCalendarHolidaysFailure(
  state: State,
  calendarHolidays: Boundaries["removeCalendarHolidays"]["failure"],
): State {
  return {
    ...state,
    calendarsHolidays: [calendarHolidays, ...state.calendarsHolidays],
  };
}

function handleCreateCalendarHolidaysRequest(
  state: State,
  { calendarHolidays }: Boundaries["createCalendarHolidays"]["request"],
): State {
  return {
    ...state,
    calendarsHolidays: [
      { ...calendarHolidays, uuid: calendarHolidays.name },
      ...state.calendarsHolidays,
    ],
  };
}

function handleCreateCalendarHolidaysSuccess(
  state: State,
  { calendarHolidays }: Boundaries["createCalendarHolidays"]["success"],
): State {
  return {
    ...state,
    calendarsHolidays: [
      calendarHolidays,
      ...state.calendarsHolidays.filter(
        ({ uuid }) => uuid !== calendarHolidays.name,
      ),
    ],
  };
}

function handleCreateCalendarHolidaysFailure(
  state: State,
  { calendarHolidays }: Boundaries["createCalendarHolidays"]["failure"],
): State {
  return {
    ...state,
    calendarsHolidays: state.calendarsHolidays.filter(
      ({ uuid }) => uuid !== calendarHolidays.name,
    ),
  };
}

function handleEditCalendarHolidaysRequest(
  state: State,
  {
    calendarHolidays,
    nextCalendarHolidays,
  }: Boundaries["editCalendarHolidays"]["request"],
): State {
  return {
    ...state,
    calendarsHolidays: state.calendarsHolidays.map((p) =>
      p.uuid === calendarHolidays.uuid
        ? { ...calendarHolidays, ...nextCalendarHolidays }
        : p,
    ),
  };
}

function handleEditCalendarHolidaysSuccess(
  state: State,
  { calendarHolidays }: Boundaries["editCalendarHolidays"]["success"],
): State {
  return {
    ...state,
    calendarsHolidays: state.calendarsHolidays.map((p) =>
      p.uuid === calendarHolidays.uuid ? calendarHolidays : p,
    ),
  };
}

function handleEditCalendarHolidaysFailure(
  state: State,
  { calendarHolidays }: Boundaries["editCalendarHolidays"]["failure"],
): State {
  return {
    ...state,
    calendarsHolidays: state.calendarsHolidays.map((p) =>
      p.uuid === calendarHolidays.uuid ? calendarHolidays : p,
    ),
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveCalendarsHolidaysRequested]: handleRequest,
    [events.retrieveCalendarsHolidaysSucceeded]: handleSuccess,
    [events.retrieveCalendarsHolidaysFailed]: handleFailure,
    [events.removeCalendarHolidaysRequested]:
      handleRemoveCalendarHolidaysRequest,
    [events.removeCalendarHolidaysSucceeded]:
      handleRemoveCalendarHolidaysSuccess,
    [events.removeCalendarHolidaysFailed]: handleRemoveCalendarHolidaysFailure,
    [events.createCalendarHolidaysRequested]:
      handleCreateCalendarHolidaysRequest,
    [events.createCalendarHolidaysSucceeded]:
      handleCreateCalendarHolidaysSuccess,
    [events.createCalendarHolidaysFailed]: handleCreateCalendarHolidaysFailure,
    [events.editCalendarHolidaysRequested]: handleEditCalendarHolidaysRequest,
    [events.editCalendarHolidaysSucceeded]: handleEditCalendarHolidaysSuccess,
    [events.editCalendarHolidaysFailed]: handleEditCalendarHolidaysFailure,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
